/*
  Stoffeya: "load more" for product grids. Markup and reasoning live in
  snippets/stoffeya-load-more.liquid.

  - Appends the next page (Section Rendering API) to the same grid.
  - Loads by itself while scrolling for the first data-auto-pages pages
    ("all" = endless, the button only shows when loading failed), after that
    only by button.
  - Going back from a product restores the pages up to that product and the
    scroll position (sessionStorage, keyed by URL), because the browser alone
    would return to page 1 and lose the shopper's place.
*/
if (!customElements.get('stoffeya-load-more')) {
  const STORE_KEY = 'stoffeya-load-more';
  // Going back reloads the pages up to the product the shopper opened (about
  // 25-30 KB each over the wire); an endless list can be far longer than that
  const MAX_RESTORE_PAGES = 30;
  const RESTORE_BATCH = 4;
  // Load once the end of the grid is within 1200px below the screen, or anywhere
  // above it: a fast flick on a phone jumps past it straight into the footer
  const AUTO_ROOT_MARGIN = '100000px 0px 1200px 0px';

  const readStore = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeStore = (store) => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch (error) {
      // Storage blocked: going back simply starts at page 1 again
    }
  };

  const currentPath = () => window.location.pathname + window.location.search;

  const isBackForward = () => performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward';

  // Collections render ul#product-grid, search renders #product-grid > ul
  const findList = (root) => {
    const grid = root.getElementById('product-grid');
    if (!grid) return null;
    return grid.matches('ul') ? grid : grid.querySelector('ul.product-grid');
  };

  // Position in the page ignoring transforms: cards slide in with an animation,
  // and getBoundingClientRect() would measure them mid-flight
  const layoutTop = (element) => {
    let top = 0;
    for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
    return top;
  };

  const fetchSection = async (url, sectionId) => {
    const target = new URL(url, window.location.href);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString());
    if (!response.ok) throw new Error(`HTTP ${response.status} for ${target.pathname}${target.search}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  class StoffeyaLoadMore extends HTMLElement {
    static restored = false;

    connectedCallback() {
      this.startPage = Number(this.dataset.page) || 1;
      this.autoPages = this.dataset.autoPages === 'all' ? Infinity : Number(this.dataset.autoPages) || 0;
      this.pageSize = findList(document)?.children.length || 1;
      this.extraPages = 0;
      this.autoUsed = 0;
      this.addEventListener('click', this.onClick);

      const saved = readStore()[currentPath()];
      if (!StoffeyaLoadMore.restored && isBackForward() && saved?.startPage === this.startPage && saved.extraPages > 0) {
        StoffeyaLoadMore.restored = true;
        this.restore(saved);
      } else {
        this.watch();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.onClick);
    }

    get button() {
      return this.querySelector('.load-more__button');
    }

    onClick = (event) => {
      if (!event.target.closest('.load-more__button')) return;
      event.preventDefault();
      this.load({ auto: false });
    };

    watch() {
      this.observer?.disconnect();
      if (!('IntersectionObserver' in window)) {
        // Endless mode hides the button; without an observer it is the only way on
        this.classList.add('load-more--manual');
        return;
      }
      if (this.autoUsed >= this.autoPages || !this.button || this.failed) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load({ auto: true });
        },
        { rootMargin: AUTO_ROOT_MARGIN }
      );
      this.observer.observe(this);
    }

    async load({ auto }) {
      const button = this.button;
      if (this.loading || !button) return;
      this.loading = true;
      this.buttonLabel ??= button.textContent.trim();
      this.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      button.textContent = button.dataset.labelLoading;

      try {
        const firstNew = this.append(await fetchSection(button.href, this.dataset.sectionId));
        this.extraPages += 1;
        // A successful retry after an error resumes loading while scrolling
        this.failed = false;
        this.classList.remove('load-more--failed');
        if (auto) this.autoUsed += 1;
        button.textContent = this.buttonLabel;
        // The browser would restore scroll against page 1 only; restore() does it instead
        history.scrollRestoration = 'manual';
        // Keyboard users continue at the first new card (its first link is a hidden duplicate)
        if (!auto) {
          const links = Array.from(firstNew?.querySelectorAll('a[href]') || []);
          links.find((link) => link.getClientRects().length > 0)?.focus({ preventScroll: true });
        }
        window.posthog?.capture?.('product_grid_load_more', {
          page: this.startPage + this.extraPages,
          auto,
        });
      } catch (error) {
        console.error('[stoffeya-load-more]', error);
        // Stop loading while scrolling and show the button with a retry text
        this.failed = true;
        this.classList.add('load-more--failed');
        button.textContent = button.dataset.labelError;
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        this.watch();
      }
    }

    async restore(saved) {
      // Pages up to the one holding the anchor card, plus one below it
      const anchorPages = Number.isFinite(saved.anchorIndex)
        ? Math.floor(saved.anchorIndex / (saved.pageSize || this.pageSize)) + 1
        : saved.extraPages;
      const pages = Math.min(saved.extraPages, anchorPages, MAX_RESTORE_PAGES);
      this.classList.add('is-loading');
      try {
        const urls = Array.from({ length: pages }, (_, index) => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', this.startPage + index + 1);
          return url;
        });
        const docs = [];
        for (let index = 0; index < urls.length; index += RESTORE_BATCH) {
          const batch = urls.slice(index, index + RESTORE_BATCH);
          docs.push(...(await Promise.all(batch.map((url) => fetchSection(url, this.dataset.sectionId)))));
        }
        docs.forEach((doc) => this.append(doc));
        this.extraPages = pages;
        this.autoUsed = Math.min(saved.autoUsed || 0, this.autoPages);
        history.scrollRestoration = 'manual';
        this.scrollBack(saved);
      } catch (error) {
        console.error('[stoffeya-load-more]', error);
      } finally {
        this.classList.remove('is-loading');
        this.watch();
      }
    }

    // Content above the grid (the stories carousel) loads late and changes
    // height, so the position is kept relative to a product card, not the page
    scrollBack(saved) {
      const anchor = findList(document)?.children[saved.anchorIndex];
      if (anchor && Number.isFinite(saved.anchorTop)) {
        window.scrollTo(0, layoutTop(anchor) - saved.anchorTop);
      } else {
        window.scrollTo(0, saved.scrollY);
      }
    }

    // First card still (partly) on screen, and how far from the top it sits
    anchor() {
      const items = Array.from(findList(document)?.children || []);
      const index = items.findIndex((item) => layoutTop(item) + item.offsetHeight > window.scrollY);
      return index < 0 ? {} : { anchorIndex: index, anchorTop: Math.round(layoutTop(items[index]) - window.scrollY) };
    }

    // Returns the first appended grid item
    append(doc) {
      const list = findList(document);
      const incoming = findList(doc);
      if (!list || !incoming) throw new Error('product grid not found in response');

      const items = Array.from(incoming.children).map((item) => document.adoptNode(item));
      list.append(...items);
      this.refreshFrom(doc.querySelector('stoffeya-load-more'));

      // Reveal-on-scroll for the new cards only (assets/animations.js)
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger({
          getElementsByClassName: (name) => items.filter((item) => item.classList.contains(name)),
        });
      }
      // Swatch "+N" counters are only computed on DOMContentLoaded and resize (product-color-swatches-nav)
      if (typeof updateShowMoreButtons === 'function') updateShowMoreButtons();

      return items[0];
    }

    // Status text, progress bar and next link come from the page just loaded
    refreshFrom(fresh) {
      const status = this.querySelector('.load-more__status');
      const freshStatus = fresh?.querySelector('.load-more__status');
      if (status && freshStatus) status.textContent = freshStatus.textContent;

      const bar = this.querySelector('.load-more__bar > span');
      const freshBar = fresh?.querySelector('.load-more__bar > span');
      if (bar && freshBar) bar.style.width = freshBar.style.width;

      const nextHref = fresh?.querySelector('.load-more__button')?.getAttribute('href');
      if (nextHref) {
        this.button?.setAttribute('href', nextHref);
      } else {
        this.button?.remove();
        this.observer?.disconnect();
      }
    }
  }

  customElements.define('stoffeya-load-more', StoffeyaLoadMore);

  window.addEventListener('pagehide', () => {
    const element = document.querySelector('stoffeya-load-more');
    const store = readStore();
    const path = currentPath();
    if (element?.extraPages > 0) {
      store[path] = {
        startPage: element.startPage,
        pageSize: element.pageSize,
        extraPages: element.extraPages,
        autoUsed: element.autoUsed,
        scrollY: Math.round(window.scrollY),
        ...element.anchor(),
        savedAt: Date.now(),
      };
    } else {
      delete store[path];
    }
    const recent = Object.entries(store)
      .sort((a, b) => b[1].savedAt - a[1].savedAt)
      .slice(0, 10);
    writeStore(Object.fromEntries(recent));
  });
}
